import {
  Allergen,
  ApiClient,
  BulkCafeDisclaimerRequest,
  Cafe,
  DraftCafeStationsSchedule,
  FileParameter,
  IBuilding,
  ICafeListItemViewModel,
  ICountry,
  ImageType,
  IMealPeriod,
  IMenuItemDb,
  IRegion,
  IRegionCafeListItem,
  IStationViewModel,
  IUploadImageResponse,
  MenuItemDb,
} from "@dining/api-client";
import { IExceptionTelemetry } from "@microsoft/applicationinsights-common";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { delayInMilliseconds, MenuPublishedState, ModalType, StationEditState } from "../common/commonConstants";
import diningClient from "../common/diningClient";
import { processMealPeriodsForUpdate } from "../common/hoursOfOperationHelper";
import { telemetryHook } from "../components/AppInsights/AppInsights";
import { UpdateCafeFormFields } from "../components/UpdateCafe/UpdateCafeForm";
import { mapUpdateCafeInputToCafe } from "../components/UpdateCafe/updateCafeHelper";
import { buildingListSelector, cafeSelector, updateCafeModeSelector } from "../selectors/diningSelector";
import { AppThunk } from "../store/store";
import { syncCafeStatus } from "./../common/commonConstants";
import { AddItemFormInput, formDataToMenuItemDb } from "./../common/draftMenuHelper";

export interface SelectedStation {
  id: string;
  name: string;
}

interface StationImage {
  stationId: string;
  imageUrl?: string;
}

export interface StationImageFileUploadRequest {
  cafeId: string;
  stationId: string;
  file: FileParameter;
}

export interface DiningState {
  buildingList: IBuilding[];
  cafeList: IRegionCafeListItem[];
  syncCafeList: IRegionCafeListItem[];
  regionList: IRegion[];
  countryList: ICountry[];
  stationList: IStationViewModel[];
  // This property is not set in certain situations, potentially something to look into.
  selectedRegion?: IRegion;
  selectedSyncRegion?: string;
  selectedCafe?: ICafeListItemViewModel;
  selectedSyncCafe?: ICafeListItemViewModel;
  selectedStation?: SelectedStation;
  selectedItem?: IMenuItemDb;
  weeklyMenu?: DraftCafeStationsSchedule;
  notificationState: MenuPublishedState | StationEditState | undefined;
  dowInfoUpdated: boolean;
  syncCafeAndFlushCache: syncCafeStatus;
  syncAuthorizationFailure: boolean;
  weekStart: string;
  cafeAllergenBank?: Allergen[];
  updateCafeMode: string;
  currentModal?: ModalType;
  panelClosed: boolean;
  isAddItemPanelOpen: boolean;
  isLoading: boolean;
  error: string | null;
  stationItemBank: IMenuItemDb[];
  menuItemBank: IMenuItemDb[];
  menuWeekDay: string;
}

export const initialState: DiningState = {
  buildingList: [],
  countryList: [],
  regionList: [],
  cafeList: [],
  syncCafeList: [],
  stationList: [],
  cafeAllergenBank: [],
  updateCafeMode: "Add",
  panelClosed: false,
  isAddItemPanelOpen: false,
  isLoading: false,
  error: null,
  menuWeekDay: "0",
  stationItemBank: [],
  menuItemBank: [],
  weekStart: dayjs().startOf("week").format("YYYY-MM-DD"),
  notificationState: undefined,
  dowInfoUpdated: false,
  syncCafeAndFlushCache: syncCafeStatus.IDLE,
  syncAuthorizationFailure: false,
};

function clearModal(state: DiningState) {
  state.currentModal = undefined;
}

function updateModal(state: DiningState, modalType: ModalType) {
  state.currentModal = modalType;
}

function startLoading(state: DiningState) {
  state.isLoading = true;
}

export function loadingFailed(state: DiningState, action: PayloadAction<IExceptionTelemetry>, errorType?: ModalType) {
  let errorName = "";
  if (!!errorType) {
    updateModal(state, errorType);
    errorName = errorType.toString();
  }
  state.isLoading = false;
  state.error = action.payload.toString();
  telemetryHook.trackException(
    { error: { name: errorName, message: state.error } },
    { "error content": action.payload },
  );
}

export function validateNull(obj: Object | undefined, errorMessage: string) {
  if (!obj) {
    throw new Error(errorMessage);
  }
}

const dining = createSlice({
  name: "dining",
  initialState,
  reducers: {
    changeUpdateCafeMode(state, { payload }: PayloadAction<string>) {
      state.updateCafeMode = payload;
    },
    flushCacheStart: startLoading,
    updateStationStart: startLoading,
    updateCafeStart: startLoading,
    addMenuItemToLocalMenuItemBank(state, { payload }: PayloadAction<IMenuItemDb>) {
      state.menuItemBank.push(payload);
    },
    deleteStationStart: startLoading,
    deleteMenuStart(state: DiningState) {
      state.notificationState = MenuPublishedState.DELETING_IN_PROGRESS;
    },
    deleteMenuItemStart: startLoading,
    getBuildingsStart: startLoading,
    getCafeStart: startLoading,
    getCafesStart: startLoading,
    getSyncCafesStart: startLoading,
    getRegionStart: startLoading,
    getRegionsStart: startLoading,
    getStationItemBankStart: startLoading,
    getStationStart: startLoading,
    getMenusForTheWeekStart: startLoading,
    getCafeAllergenBankStart: startLoading,
    saveCafeScheduleStart: startLoading,
    addItemToBankStart: startLoading,
    updateDisclaimerStart: startLoading,
    publishMenuStart(state: DiningState) {
      state.notificationState = MenuPublishedState.PUBLISHING_IN_PROGRESS;
    },
    updateDraftScheduleStart: startLoading,
    updateCafeHoursStart: startLoading,
    setNotification(state: DiningState, { payload }: PayloadAction<MenuPublishedState | undefined | StationEditState>) {
      state.notificationState = payload;
    },
    setDowUpdatedFlag(state: DiningState, { payload }: PayloadAction<boolean>) {
      state.dowInfoUpdated = payload;
    },
    setSyncCafeAndFlushCache(state: DiningState, { payload }: PayloadAction<syncCafeStatus>) {
      state.syncCafeAndFlushCache = payload;
    },
    setSyncAuthorizationFailure(state: DiningState, { payload }: PayloadAction<boolean>) {
      state.syncAuthorizationFailure = payload;
    },
    setSelectedStation(state, { payload }: PayloadAction<SelectedStation>) {
      state.selectedStation = payload;
      state.isLoading = false;
      state.error = null;
    },
    deleteStationSuccess(state) {
      clearModal(state);
      state.selectedStation = undefined;
    },
    deleteMenuSuccess(state) {
      clearModal(state);
      state.dowInfoUpdated = false;
      state.notificationState = MenuPublishedState.MENU_DELETED;
      state.selectedItem = undefined;
    },
    deleteMenuItemSuccess(state) {
      clearModal(state);
      state.selectedItem = undefined;
    },
    flushCacheSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    getBuildingsSuccess(state, { payload }: PayloadAction<IBuilding[]>) {
      state.buildingList = payload;
      state.isLoading = false;
      state.error = null;
    },
    setSelectedCafe(state, { payload }: PayloadAction<ICafeListItemViewModel>) {
      state.selectedCafe = payload;
      state.selectedStation = undefined;
      state.weeklyMenu = undefined;
      state.cafeAllergenBank = [];
      state.isLoading = false;
      state.error = null;
    },
    setSelectedSyncCafeStart(state, { payload }: PayloadAction<ICafeListItemViewModel>) {
      state.selectedSyncCafe = payload;
      state.selectedStation = undefined;
      state.weeklyMenu = undefined;
      state.cafeAllergenBank = [];
      state.isLoading = false;
      state.error = null;
    },
    setSelectedSyncRegion(state, { payload }: PayloadAction<string>) {
      state.selectedSyncRegion = payload;
    },
    setStationList(state, { payload }: PayloadAction<IStationViewModel[]>) {
      state.stationList = payload;
      state.selectedStation = undefined;
      // Note: weeklyMenu is intentionally NOT reset here to avoid a race condition.
      // The weeklyMenu is already reset in setSelectedCafe when a new cafe is selected,
      // and fetching the station list should not clear the menu data that may have
      // already been fetched by fetchCafeMenusForTheWeek.
      state.isLoading = false;
      state.error = null;
    },
    getCafesSuccess(state, { payload }: PayloadAction<IRegionCafeListItem[]>) {
      state.cafeList = payload;
      state.selectedCafe = undefined;
      state.stationList = [];
      state.weeklyMenu = undefined;
      state.cafeAllergenBank = [];
      state.isLoading = false;
      state.error = null;
    },
    getSyncCafesSuccess(state, { payload }: PayloadAction<IRegionCafeListItem[]>) {
      state.syncCafeList = payload;
      state.selectedSyncCafe = undefined;
      state.stationList = [];
      state.weeklyMenu = undefined;
      state.cafeAllergenBank = [];
      state.isLoading = false;
      state.error = null;
    },
    getRegionSuccess(state, { payload }: PayloadAction<IRegion>) {
      state.selectedRegion = payload;
      state.isLoading = false;
      state.error = null;
    },
    getRegionsSuccess(state, { payload }: PayloadAction<IRegion[]>) {
      state.regionList = payload;
      state.cafeList = [];
      state.selectedCafe = undefined;
      state.stationList = [];
      state.selectedStation = undefined;
      state.weeklyMenu = undefined;
      state.cafeAllergenBank = [];
      state.isLoading = false;
      state.error = null;
    },
    getMenusForTheWeekSuccess(state, { payload }: PayloadAction<DraftCafeStationsSchedule>) {
      state.weeklyMenu = payload;
      state.isLoading = false;
      state.error = null;
    },
    getMenuItemsForTheWeekSuccess(state, { payload }: PayloadAction<MenuItemDb[]>) {
      state.menuItemBank = payload;
    },
    getCafeAllergenBankSuccess(state, { payload }: PayloadAction<Allergen[]>) {
      state.cafeAllergenBank = payload;
      state.isLoading = false;
      state.error = null;
    },
    getStationItemBankSuccess(state, { payload }: PayloadAction<IMenuItemDb[]>) {
      state.stationItemBank = payload;
      state.isLoading = false;
      state.error = null;
    },
    updateDraftScheduleSuccess(state, { payload }: PayloadAction<DraftCafeStationsSchedule>) {
      state.isLoading = false;
      state.selectedItem = undefined;
      state.error = null;
    },
    updateDisclaimerSuccess(state) {
      state.isLoading = false;
      state.selectedItem = undefined;
      state.error = null;
    },
    updateCafeHoursSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    setStartOfWeek(state, { payload }: PayloadAction<Date>) {
      state.weekStart = dayjs(payload).format("YYYY-MM-DD");
    },
    setSelectedItem(state, { payload }: PayloadAction<IMenuItemDb | undefined>) {
      state.selectedItem = payload;
    },
    updateCafeSuccess(state) {
      state.isLoading = false;
      state.error = null;
      updateModal(state, ModalType.UPDATE_CAFE_SUCCESS);
    },
    addStationSuccess(state, { payload }: PayloadAction<IStationViewModel[]>) {
      state.error = null;
      state.stationList = payload;
    },
    addItemToBankSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
    },
    publishMenuSuccess(state) {
      state.dowInfoUpdated = false;
      state.isLoading = false;
      state.error = null;
    },
    deleteStationFailure(state, action) {
      loadingFailed(state, action, ModalType.DELETE_STATION_FAILURE);
    },
    deleteMenuFailure(state, action) {
      loadingFailed(state, action, ModalType.DELETE_MENU_FAILURE);
    },
    deleteMenuItemFailure(state, action) {
      loadingFailed(state, action, ModalType.DELETE_MENU_ITEM_FAILURE);
    },
    deleteImageByUrlFailure(state, action) {
      loadingFailed(state, action, ModalType.DELETE_IMAGE_FAILURE);
    },
    flushCacheFailure: loadingFailed,
    getBuildingsFailure: loadingFailed,
    getCafeFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_CAFE_FAILURE);
    },
    getSyncCafeFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_CAFE_FAILURE);
    },
    getCafesFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_CAFE_LIST_FAILURE);
    },
    getSyncCafesFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_CAFE_LIST_FAILURE);
    },
    getRegionFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_REGION_FAILURE);
    },
    getRegionsFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_REGION_LIST_FAILURE);
    },
    getStationFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_STATION_FAILURE);
    },
    getMenusForTheWeekFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_MENU_FAILURE);
    },
    getCafeAllergenBankFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_CAFE_ALLERGEN_BANK_FAILURE);
    },
    getStationItemBankFailure(state, action) {
      loadingFailed(state, action, ModalType.LOAD_STATION_ITEM_BANK_FAILURE);
    },
    saveCafeScheduleFailure(state, action) {
      loadingFailed(state, action, ModalType.SAVE_MENU_FAILURE);
    },
    updateCafeFailure(state, action) {
      loadingFailed(state, action, ModalType.UPDATE_CAFE_FAILURE);
    },
    publishMenuFailure(state, action) {
      loadingFailed(state, action, ModalType.PUBLISH_MENU_FAILURE);
    },
    addStationFailure(state, action) {
      loadingFailed(state, action, ModalType.ADD_STATION_FAILURE);
    },
    updateStationFailure(state, action) {
      loadingFailed(state, action);
    },
    addItemToBankFailure(state, action) {
      loadingFailed(state, action, ModalType.ADD_ITEM_TO_BANK_FAILURE);
    },
    updateDisclaimerFailure(state, action) {
      loadingFailed(state, action, ModalType.UPDATE_DISCLAIMER_FAILURE);
    },
    updateCafeHoursFailure(state, action) {
      loadingFailed(state, action);
    },
    setModal(state, { payload }: PayloadAction<ModalType>) {
      updateModal(state, payload);
    },
    closeModal: clearModal,
    toggleAddItemPanel(state) {
      state.isAddItemPanelOpen = !state.isAddItemPanelOpen;
    },
    openPanel(state) {
      state.panelClosed = false;
    },
    closePanel(state) {
      state.panelClosed = true;
    },
    confirmStationDelete(state) {
      updateModal(state, ModalType.CONFIRM_DELETE_STATION);
    },
    confirmPublishMenu(state) {
      updateModal(state, ModalType.CONFIRM_PUBLISH_MENU);
    },
    confirmDeleteMenu(state) {
      updateModal(state, ModalType.CONFIRM_DELETE_MENU);
    },
    selectMenuWeekDay(state, { payload }: PayloadAction<string>) {
      state.menuWeekDay = payload;
    },
    setUpdatedCafeCountries(state, { payload }: PayloadAction<ICountry[]>) {
      state.countryList = payload;
    },
    uploadStationImageSuccess(state, { payload }: PayloadAction<StationImage>) {
      state.stationList = state.stationList.map((station) => {
        if (station.id === payload.stationId) {
          station.imageUrl = payload.imageUrl;
        }

        return station;
      });
    },
    invalidFileForImageUpload(state) {
      updateModal(state, ModalType.IMAGE_UPLOAD_FAILURE);
      state.error = "Invalid file for image upload";
    },
    uploadStationImageFailure(state, action) {
      loadingFailed(state, action, ModalType.IMAGE_UPLOAD_FAILURE);
    },
    deleteStationImageFailure(state, action) {
      loadingFailed(state, action, ModalType.IMAGE_DELETE_FAILURE);
    },
    updateDraftScheduleFailure(state, action) {
      loadingFailed(state, action, ModalType.UPDATE_DRAFT_MENU_FAILURE);
    },
  },
});

export const {
  setNotification,
  setDowUpdatedFlag,
  setSelectedStation,
  changeUpdateCafeMode,
  addMenuItemToLocalMenuItemBank,
  deleteMenuStart,
  deleteStationStart,
  deleteMenuItemStart,
  getBuildingsStart,
  getCafeStart,
  getCafesStart,
  getSyncCafesStart,
  getRegionStart,
  getRegionsStart,
  getStationStart,
  getMenusForTheWeekStart,
  getCafeAllergenBankStart,
  getStationItemBankStart,
  saveCafeScheduleStart,
  updateCafeStart,
  updateStationStart,
  addItemToBankStart,
  publishMenuStart,
  updateDraftScheduleStart,
  updateCafeHoursStart,
  updateDisclaimerStart,
  deleteStationSuccess,
  deleteMenuSuccess,
  deleteMenuItemSuccess,
  getBuildingsSuccess,
  getCafesSuccess,
  getSyncCafesSuccess,
  setSelectedCafe,
  setSelectedSyncCafeStart,
  setSelectedSyncRegion,
  setSyncCafeAndFlushCache,
  setSyncAuthorizationFailure,
  setStationList,
  getRegionSuccess,
  getRegionsSuccess,
  getMenusForTheWeekSuccess,
  getMenuItemsForTheWeekSuccess,
  getCafeAllergenBankSuccess,
  getStationItemBankSuccess,
  updateCafeSuccess,
  addItemToBankSuccess,
  publishMenuSuccess,
  updateDraftScheduleSuccess,
  updateDisclaimerSuccess,
  updateCafeHoursSuccess,
  deleteStationFailure,
  deleteMenuFailure,
  deleteMenuItemFailure,
  getBuildingsFailure,
  getCafeFailure,
  getSyncCafeFailure,
  getCafesFailure,
  getSyncCafesFailure,
  getRegionFailure,
  getRegionsFailure,
  getStationFailure,
  getMenusForTheWeekFailure,
  getCafeAllergenBankFailure,
  getStationItemBankFailure,
  saveCafeScheduleFailure,
  publishMenuFailure,
  updateDraftScheduleFailure,
  setStartOfWeek,
  setSelectedItem,
  updateCafeFailure,
  updateStationFailure,
  addStationSuccess,
  addStationFailure,
  addItemToBankFailure,
  updateDisclaimerFailure,
  updateCafeHoursFailure,
  setModal,
  closeModal,
  openPanel,
  closePanel,
  toggleAddItemPanel,
  confirmStationDelete,
  flushCacheStart,
  flushCacheSuccess,
  flushCacheFailure,
  selectMenuWeekDay,
  setUpdatedCafeCountries,
  uploadStationImageSuccess,
  invalidFileForImageUpload,
  uploadStationImageFailure,
  deleteStationImageFailure,
  confirmPublishMenu,
  confirmDeleteMenu,
  deleteImageByUrlFailure,
} = dining.actions;

export default dining.reducer;

export const uploadStationImage =
  (cafeId: string, stationId: string, file: FileParameter): AppThunk =>
  async (dispatch) => {
    try {
      const imageUrl = await diningClient.uploadImage(cafeId, stationId, "1.0", file);
      dispatch(uploadStationImageSuccess({ stationId, imageUrl }));
    } catch (err) {
      dispatch(uploadStationImageFailure(err));
    }
  };

export const deleteStationImage =
  (cafeId: string, stationId: string): AppThunk =>
  async (dispatch) => {
    try {
      await diningClient.deleteImage(cafeId, stationId);
      dispatch(uploadStationImageSuccess({ stationId }));
    } catch (err) {
      dispatch(deleteStationImageFailure(err));
    }
  };

export const syncCafeAndFlushCacheByIdAsync =
  (cafeId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setSyncCafeAndFlushCache(syncCafeStatus.PROGRESS));

      await diningClient.syncCafeId(cafeId);
      dispatch(setSyncCafeAndFlushCache(syncCafeStatus.SUCCESS));
    } catch (err) {
      if (err.subStatusCode === syncCafeStatus.NOTAUTHORIZED) {
        dispatch(setSyncAuthorizationFailure(true));
      } else {
        dispatch(setSyncCafeAndFlushCache(syncCafeStatus.ERROR));
      }
    }
  };

export const resetSyncCafeAndFlushCacheStatus =
  (syncStatus: syncCafeStatus): AppThunk =>
  async (dispatch) => {
    dispatch(setSyncCafeAndFlushCache(syncStatus));
  };

export const fetchBuildings =
  (countryCode?: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getBuildingsStart());
      const response = await diningClient.getAllBuildings(undefined, undefined, countryCode);
      const result = response ?? [];
      dispatch(
        getBuildingsSuccess(result.sort((a, b) => a.name.localeCompare(b.name)).map((c) => c.toJSON() as IBuilding)),
      );
    } catch (err) {
      dispatch(getBuildingsFailure(err.toString()));
    }
  };

export const fetchRegionsAsync = (): AppThunk => async (dispatch) => {
  try {
    dispatch(getRegionsStart());
    const response = await diningClient.getAllRegions(100, 0);
    const result = response ?? [];
    dispatch(
      getRegionsSuccess(
        result.map((r) => {
          r.countries?.sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : -1));
          return r.toJSON();
        }),
      ),
    );
  } catch (err) {
    dispatch(getRegionsFailure(err));
  }
};

export const fetchRegionCafesAsync =
  (regionId: string, vendorName?: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getCafesStart());
      const response = await diningClient.getCafesByRegionId(regionId, vendorName);
      const result = response ?? [];
      dispatch(
        getCafesSuccess(
          result.sort((a, b) => a.name.localeCompare(b.name)).map((c) => c.toJSON() as IRegionCafeListItem),
        ),
      );
    } catch (err) {
      dispatch(getCafesFailure(err));
    }
  };

export const fetchRegionSyncCafesAsync =
  (regionId: string, vendorName?: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getSyncCafesStart());

      const response = await diningClient.getCafesByRegionId(regionId, vendorName);
      const result = response ?? [];
      const syncResult = result.filter((res) => res.isDecommissioned !== true);

      dispatch(
        getSyncCafesSuccess(
          syncResult.sort((a, b) => a.name.localeCompare(b.name)).map((c) => c.toJSON() as IRegionCafeListItem),
        ),
      );
    } catch (err) {
      dispatch(getSyncCafesFailure(err));
    }
  };
const fetchCafeAndDispatchCommon =
  (id: string, cafeType: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getCafeStart());
      const cafe = await diningClient.getCafeById(id);
      if (cafeType == "sync") {
        dispatch(setSelectedSyncCafeAsync(cafe.toJSON()));
      } else {
        dispatch(setSelectedCafe(cafe.toJSON()));
        dispatch(changeUpdateCafeMode("Edit"));
      }
    } catch (err) {
      dispatch(getCafeFailure(err));
    }
  };
// Updated async thunk
export const fetchSelectedCafeAndStationsAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      await dispatch(fetchCafeAndDispatchCommon(id, "load"));
      const stationList = await diningClient.getCafeStationsList(id);
      dispatch(setStationList(stationList.items?.map((item) => item.toJSON()) || []));
    } catch (err) {
      dispatch(getCafeFailure(err));
    }
  };

// Updated async thunk
export const fetchSelectedSyncCafeAsync =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      await dispatch(fetchCafeAndDispatchCommon(id, "sync"));
    } catch (err) {
      dispatch(getCafeFailure(err));
    }
  };

export const setSelectedSyncRegionAsync =
  (region: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setSelectedSyncRegion(region));
    } catch (err) {
      dispatch(getRegionFailure(err));
    }
  };

export const setSelectedSyncCafeAsync =
  (cafe: ICafeListItemViewModel): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setSelectedSyncCafeStart(cafe));
    } catch (err) {
      dispatch(getSyncCafeFailure(err));
    }
  };

export const fetchCafeMenusForTheWeek =
  (cafeId: string, weekStart: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getMenusForTheWeekStart());
      const response = await diningClient.getCafeWeeklySchedule(cafeId, weekStart);
      const scheduleResult = response?.draftCafeStationsSchedule?.toJSON();
      const menuItemBankResult = response?.menuItems?.map((item) => item.toJSON());
      scheduleResult && dispatch(getMenusForTheWeekSuccess(scheduleResult));
      menuItemBankResult && dispatch(getMenuItemsForTheWeekSuccess(menuItemBankResult));
    } catch (err) {
      dispatch(getMenusForTheWeekFailure(err));
    }
  };

export const fetchStationItemBank =
  (stationId: string, cafeId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStationItemBankStart());
      const response = await diningClient.getMenuBankForStation(cafeId, stationId);
      dispatch(getStationItemBankSuccess((response || []).map((a) => a.toJSON() as IMenuItemDb)));
    } catch (err) {
      dispatch(getStationItemBankFailure(err));
    }
  };

export const fetchCafeAllergenBank =
  (cafeId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getCafeAllergenBankStart());
      const response = await diningClient.getAllergens(cafeId);
      dispatch(getCafeAllergenBankSuccess((response || []).map((a) => a.toJSON() as Allergen)));
    } catch (err) {
      dispatch(getCafeAllergenBankFailure(err));
    }
  };

export const flushCache = (): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(flushCacheStart());
    await diningClient.flushCache();
    dispatch(flushCacheSuccess());
  } catch (err) {
    console.log(err);
    dispatch(flushCacheFailure(err));
  }
};

export const updateCafe =
  (data: UpdateCafeFormFields): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(updateCafeStart());
      const state = getState();
      const buildingList: IBuilding[] | undefined = buildingListSelector(state);
      const updateCafeMode: string = updateCafeModeSelector(state);
      const selectedCafe: ICafeListItemViewModel | undefined = cafeSelector(state);
      const updateSelectedBuilding: IBuilding | undefined = buildingList.find(
        (building) => building.id === data.cafeLocation,
      );
      const item: ICafeListItemViewModel = mapUpdateCafeInputToCafe(
        data,
        updateCafeMode === "Edit" ? selectedCafe : undefined,
        updateSelectedBuilding,
      );
      await diningClient.createCafe(Cafe.fromJS(item).toJSON(), data.cafeRegion);
      dispatch(flushCache());
      dispatch(fetchRegionCafesAsync(data.cafeRegion));
      dispatch(updateCafeSuccess());
    } catch (err) {
      console.log(err);
      dispatch(updateCafeFailure(err));
    }
  };

export const addStation =
  (name: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(updateCafeStart());
      dispatch(setNotification(MenuPublishedState.LOADING));
      const state = getState();
      if (state.dining.selectedCafe == null) {
        throw new Error("Cafe should be selected");
      }
      const result = await diningClient.addStation(state.dining.selectedCafe?.id, name);
      dispatch(addStationSuccess([...state.dining.stationList, result.toJSON() as IStationViewModel]));
      dispatch(setNotification(StationEditState.STATION_CREATED_SUCCESSFULLY));
    } catch (err) {
      console.log(err);
      dispatch(addStationFailure(err));
    }
  };

export const updateStation =
  (newName: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(updateStationStart());
      validateNull(state.dining.selectedCafe, "No cafe was selected");
      validateNull(state.dining.selectedStation, "No station was selected");
      const result = await diningClient.renameStation(
        state.dining.selectedCafe!.id,
        state.dining.selectedStation!.id,
        newName,
      );
      dispatch(setSelectedStation(result));
    } catch (err) {
      dispatch(updateStationFailure(JSON.stringify(err)));
    }
    await dispatch(fetchSelectedCafeAndStationsAsync(state.dining.selectedCafe!.id));
    await dispatch(fetchCafeMenusForTheWeek(state.dining.selectedCafe!.id, state.dining.weekStart));
  };

export const deleteStation = (): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(deleteStationStart());
    dispatch(setNotification(MenuPublishedState.LOADING));
    const state = getState();
    if (state.dining.selectedStation == null) {
      throw new Error("No station was selected");
    }
    if (state.dining.selectedCafe == null) {
      throw new Error("No cafe was selected");
    }
    await diningClient.deleteStation(state.dining.selectedCafe.id, state.dining.selectedStation.id);
    dispatch(deleteStationSuccess());
    dispatch(setNotification(StationEditState.STATION_DELETED_SUCCESSFULLY));
    // For accessibility purposes, setting a 1.5-second delay to give users sufficient time to read the previous notification before the next one replaces it.
    setTimeout(() => {
      if (state.dining.selectedCafe) {
        dispatch(fetchSelectedCafeAndStationsAsync(state.dining.selectedCafe.id));
      }
    }, delayInMilliseconds);
  } catch (err) {
    dispatch(deleteStationFailure(err));
  }
};

// Takes AddItemFormInputs and Image data, and uploads them to the database
// Refreshes the menu preview
export const addItemToBank =
  (
    cafeId: string,
    stationId: string,
    itemToAdd: AddItemFormInput,
    currentWeek: string,
    isEditedItem: boolean,
  ): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(addItemToBankStart());
      dispatch(setNotification(MenuPublishedState.LOADING));
      // Convert our form input into an IMenuItemDb object
      let menuItem: IMenuItemDb = formDataToMenuItemDb(itemToAdd, stationId);
      // If we've been told to delete an image, do so...
      if (itemToAdd.image?.deleteUrl) {
        try {
          await diningClient.deleteImageByUrl(ImageType.MenuItem, itemToAdd.image?.deleteUrl);
        } catch (err) {
          dispatch(deleteImageByUrlFailure(JSON.stringify(err)));
        }
      }
      // If there's a new file for the image...
      if (itemToAdd?.image?.file) {
        // Create the payload for the new image...
        const fileParameter: FileParameter = { data: itemToAdd.image?.file, fileName: uuidv4() + ".jpeg" };
        // Upload the image and wait for the response so we can add the URL to the menuBankItem...
        const response: IUploadImageResponse = await diningClient.uploadImageForMenuItem(
          cafeId,
          stationId,
          itemToAdd?.id ? itemToAdd.id : uuidv4(),
          "1.0",
          fileParameter,
        );
        // Add the new URI for the item image to the item.
        menuItem.imageUrl = response.imageUrl;
      }
      const finalForm = new MenuItemDb(menuItem).toJSON();
      await diningClient.addUpdateMenuItemInMenuBank(cafeId, stationId, finalForm);
      dispatch(addItemToBankSuccess(finalForm));
    } catch (err) {
      dispatch(addItemToBankFailure(JSON.stringify(err)));
    }
    dispatch(setSelectedItem(undefined));
    if (!isEditedItem) {
      dispatch(setNotification(MenuPublishedState.ITEM_ADDED_TO_BANK_SUCCESSFULLY));
    } else {
      dispatch(setNotification(MenuPublishedState.ITEM_EDITED_IN_BANK_SUCCESSFULLY));
    }
    // For accessibility purposes, setting a 1.5-second delay to give users sufficient time to read the previous notification before the next one replaces it.
    setTimeout(() => {
      dispatch(fetchStationItemBank(stationId, cafeId));
      dispatch(fetchCafeMenusForTheWeek(cafeId, state.dining.weekStart.toString()));
    }, delayInMilliseconds);
  };

export const deleteMenuItem = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  try {
    dispatch(setNotification(MenuPublishedState.LOADING));
    dispatch(deleteMenuItemStart());
    validateNull(state.dining.selectedItem, "No menu item was selected");
    validateNull(state.dining.selectedCafe, "No cafe was selected");
    validateNull(state.dining.selectedStation, "No station was selected");

    await diningClient.deleteMenuItemFromMenuBank(
      state.dining.selectedCafe!.id,
      state.dining.selectedStation!.id,
      state.dining.selectedItem!.id,
    );
    dispatch(deleteMenuItemSuccess());
  } catch (err) {
    dispatch(deleteMenuItemFailure(err));
  }
  dispatch(setNotification(MenuPublishedState.ITEM_REMOVE_FROM_BANK_SUCCESSFULLY));
  // For accessibility purposes, setting a 1.5-second delay to give users sufficient time to read the previous notification before the next one replaces it.
  setTimeout(() => {
    dispatch(fetchStationItemBank(state.dining.selectedStation!.id, state.dining.selectedCafe!.id));
    dispatch(fetchCafeMenusForTheWeek(state.dining.selectedCafe!.id, state.dining.weekStart.toString()));
  }, delayInMilliseconds);
};

export const updateDraftSchedule =
  (draftSchedule: DraftCafeStationsSchedule): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(updateDraftScheduleStart());
      validateNull(state.dining.selectedCafe, "No cafe was selected");
      const result = await diningClient.addOrUpdateCafeStationWeekSchedule(
        state.dining.selectedCafe!.id,
        draftSchedule,
      );
      dispatch(updateDraftScheduleSuccess(result.toJSON()));
    } catch (err) {
      dispatch(updateDraftScheduleFailure(JSON.stringify(err)));
    }

    dispatch(setNotification(MenuPublishedState.ITEM_SAVED_TO_MENU_SUCCESSFULLY));
    setTimeout(() => {
      dispatch(fetchCafeMenusForTheWeek(state.dining.selectedCafe!.id, state.dining.weekStart.toString()));
    }, delayInMilliseconds);
  };

export const publishMenu = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  try {
    dispatch(publishMenuStart());
    validateNull(state.dining.selectedCafe, "No cafe selected.");
    validateNull(state.dining.weeklyMenu, "No menu object to update with");
    await diningClient.publishCafeWeeklySchedule(state.dining.selectedCafe!.id, state.dining.weekStart!.toString());
    dispatch(publishMenuSuccess());
  } catch (err) {
    dispatch(publishMenuFailure(err));
  }
  dispatch(fetchCafeMenusForTheWeek(state.dining.selectedCafe!.id, state.dining.weekStart.toString()));
  dispatch(setNotification(MenuPublishedState.CHANGES_PUBLISHED_SUCCESSFULLY));
};

export const deleteMenu = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  try {
    dispatch(deleteMenuStart());

    validateNull(state.dining.weeklyMenu, "No existing menu to delete");
    validateNull(state.dining.weekStart, "We can't delete a menu without a selected week.");
    validateNull(state.dining.selectedCafe, "No cafe selected.");

    await diningClient.deleteDraftCafeStationWeekSchedule(
      state.dining.selectedCafe!.id,
      state.dining.weeklyMenu!.weekStart.toString(),
    );
    dispatch(deleteMenuSuccess());
  } catch (err) {
    dispatch(deleteMenuFailure(err));
  }
  dispatch(fetchCafeMenusForTheWeek(state.dining.selectedCafe!.id, state.dining.weekStart.toString()));
};
export const UpdateDisclaimer =
  (cafeId: string, disclaimer: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateDisclaimerStart());
      const bulkCafeDisclaimerRequest: BulkCafeDisclaimerRequest = {
        cafeIds: [cafeId],
        disclaimer: disclaimer,
      } as BulkCafeDisclaimerRequest;
      await (diningClient as ApiClient).disclaimers(undefined, bulkCafeDisclaimerRequest);
      // Refresh cafe data
      const cafe = await diningClient.getCafeById(cafeId).catch(() => undefined);
      if (cafe) {
        dispatch(setSelectedSyncCafeStart(cafe.toJSON()));
      }
      dispatch(updateDisclaimerSuccess());
    } catch (err) {
      dispatch(updateDisclaimerFailure(err));
    }
  };

export const updateCafeHoursOfOperation =
  (cafeId: string, mealPeriods: IMealPeriod[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateCafeHoursStart());

      // Process meal periods using the extracted helper function
      const cleanMealPeriods = processMealPeriodsForUpdate(mealPeriods);

      await (diningClient as any).hoursofoperation([cafeId], undefined, cleanMealPeriods);

      // Refresh cafe data
      const cafe = await diningClient.getCafeById(cafeId).catch(() => undefined);
      if (cafe) {
        dispatch(setSelectedSyncCafeStart(cafe.toJSON()));
      }

      dispatch(updateCafeHoursSuccess());
    } catch (err) {
      dispatch(updateCafeHoursFailure(err));
    }
  };
